import { t, useLocale } from "@/shared/i18n/locale";
import { LanguageSelect } from "@/shared/i18n/language-select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { z } from "zod";
import { useLogin } from "@/features/auth/use-auth";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { toast } from "sonner";
import { Shield, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";

const schema = z.object({
  email: z.string().email("Informe um email valido"),
  password: z.string().min(1, "Informe a senha"),
});

type FormData = z.infer<typeof schema>;

export function LoginPage() {
  useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(data: FormData) {
    try {
      await login.mutateAsync(data);
      const target = (location.state as { from?: string } | undefined)?.from ?? "/";
      navigate(target, { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("Falha no login"));
    }
  }

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="absolute right-6 top-6 z-20"><LanguageSelect /></div>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />
        <div className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/5 blur-3xl" />
      </div>

      <Card className="relative w-full max-w-md border-0 bg-[var(--card)] shadow-2xl backdrop-blur-sm">
        <div className="p-8">
          {/* Logo and Header */}
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg shadow-blue-500/30">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[var(--foreground)]">Pingys Admin</h1>
            <p className="mt-2 text-sm text-[var(--muted-foreground)]">
              {t("Acesso exclusivo para administradores")}</p>
          </div>

          {/* Login Form */}
          <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[var(--foreground)]">
                {t("Email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@pingys.com"
                autoComplete="email"
                disabled={login.isPending}
                className="h-11"
                {...form.register("email")}
              />
              {form.formState.errors.email ? (
                <p className="flex items-center gap-1 text-xs text-[var(--rose-text)]">
                  <span className="inline-block h-1 w-1 rounded-full bg-rose-600" />
                  {t(form.formState.errors.email.message ?? "")}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[var(--foreground)]">
                {t("Senha")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={login.isPending}
                  className="h-11 pr-10"
                  {...form.register("password")}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] transition-colors hover:text-[var(--muted-foreground)]"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {form.formState.errors.password ? (
                <p className="flex items-center gap-1 text-xs text-[var(--rose-text)]">
                  <span className="inline-block h-1 w-1 rounded-full bg-rose-600" />
                  {t(form.formState.errors.password.message ?? "")}
                </p>
              ) : null}
            </div>

            <Button
              className="h-11 w-full bg-gradient-to-r from-blue-600 to-blue-700 font-semibold shadow-lg shadow-blue-500/30 transition-all hover:shadow-xl hover:shadow-blue-500/40 disabled:opacity-50"
              type="submit"
              disabled={login.isPending}
            >
              {login.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("Entrando...")}</>
              ) : (
                t("Entrar")
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 border-t border-[var(--border)] pt-5">
            <p className="text-center text-xs text-[var(--muted-foreground)]">
              {t("Area restrita - Somente usuarios com perfil administrativo podem acessar")}</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
